// ark-swipe-action.js
// iOS UISwipeActionsConfiguration 风格的滑动手势组件控制器。
// 仅触屏（pointerType in {'touch','pen'}），桌面鼠标完全 no-op。

const ACTIVATION_THRESHOLD = 10;     // px，开始水平识别
const OVERSHOOT_RATIO = 0.6;          // 过冲触发主动作的卡片宽度比
const SNAP_OPEN_RATIO = 0.3;          // snap 打开门槛（占动作宽度比）
const FAST_SWIPE_VELOCITY = 0.3;      // px/ms，速度判定门槛
const CLICK_TOLERANCE = 5;            // px，移动 < 该值视为点击
const TRANSITION = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
const SCROLL_CLOSE_THROTTLE = 100;    // ms

// rubber band 阻尼公式（iOS）
const RB = (over, dim) => over * dim / (dim + over);

// 全局打开实例 registry
const openInstances = new Set();

// 动效关闭（宿主开关或系统「减少动态效果」，由 ark-motion.js 合并成 data-motion=off）→ 直接落位
const reducedMotion = () => window.ArkUI?.Motion
    ? !window.ArkUI.Motion.enabled()
    : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// 找最近的 scroll 父级
function findScrollParent(el) {
    let p = el.parentElement;
    while (p && p !== document.body) {
        const s = getComputedStyle(p);
        if (/(auto|scroll|overlay)/.test(s.overflowY + s.overflow)) return p;
        p = p.parentElement;
    }
    return window;
}

class SwipeController {
    constructor(rootEl, dotnetRef) {
        this.root = rootEl;
        this.content = rootEl.querySelector('[data-swipe-content]');
        this.leftEl = rootEl.querySelector('[data-swipe-left]');
        this.rightEl = rootEl.querySelector('[data-swipe-right]');
        this.dotnet = dotnetRef;

        // 状态
        this.translate = 0;
        this.startX = 0;
        this.startY = 0;
        this.lastX = 0;
        this.lastT = 0;
        this.velocity = 0;
        this.locked = null;          // null | 'horizontal' | 'vertical'
        this.dragging = false;
        this.opened = false;
        this.openSide = null;        // 'left' | 'right' | null
        this.startTranslate = 0;
        this._lastWillTrigger = false;
        this._activePointerId = null;

        // 缓存动作宽度（ResizeObserver 失效时刷新）
        this.refreshDimensions();

        // 绑定方法
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onContextMenu = this.onContextMenu.bind(this);

        // 事件订阅
        this.root.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.root.addEventListener('contextmenu', this.onContextMenu);

        this.scrollParent = findScrollParent(this.root);
        this._scrollLast = 0;

        // ResizeObserver
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.refreshDimensions());
            this.resizeObserver.observe(this.root);
        }
    }

    refreshDimensions() {
        this.cardW = this.root.offsetWidth || 320;
        // 从 data-total-width 读，不依赖 scrollWidth（width:0 + overflow-hidden 下不可靠）
        this.leftWidth = this.leftEl ? (parseInt(this.leftEl.dataset.totalWidth, 10) || 0) : 0;
        this.rightWidth = this.rightEl ? (parseInt(this.rightEl.dataset.totalWidth, 10) || 0) : 0;
    }

    // ───────────── pointer 事件 ─────────────

    onPointerDown(e) {
        // 仅触屏
        if (e.pointerType === 'mouse') return;
        if (e.button !== 0) return;
        // 点在动作槽内不触发拖动（点击按钮）
        if (e.target.closest('[data-swipe-left], [data-swipe-right]')) return;

        // 实时刷新尺寸（应对 Item 变化或窗口尺寸变化）
        this.refreshDimensions();

        this._activePointerId = e.pointerId;
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.lastX = e.clientX;
        this.lastT = e.timeStamp;
        this.velocity = 0;
        this.locked = null;
        this.dragging = true;
        this.startTranslate = this.translate;
        this._lastWillTrigger = false;

        // 阻断进行中的过渡
        this.content.style.transition = 'none';

        this.root.addEventListener('pointermove', this.onPointerMove, { passive: false });
        this.root.addEventListener('pointerup', this.onPointerUp);
        this.root.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(e) {
        if (!this.dragging || e.pointerId !== this._activePointerId) return;
        const dx = e.clientX - this.startX;
        const dy = e.clientY - this.startY;

        // 方向仲裁
        if (this.locked === null) {
            const adx = Math.abs(dx), ady = Math.abs(dy);
            if (adx < ACTIVATION_THRESHOLD && ady < ACTIVATION_THRESHOLD) return;
            if (ady > adx) {
                // 垂直手势 → 让浏览器滚动
                this.locked = 'vertical';
                this.cleanupActive();
                return;
            }
            this.locked = 'horizontal';
            try { this.root.setPointerCapture(e.pointerId); } catch { /* ignore */ }
            // 关闭其他打开的实例
            for (const inst of openInstances) if (inst !== this) inst.close(false);
        }

        if (this.locked !== 'horizontal') return;
        e.preventDefault();

        // 速度估算
        const now = e.timeStamp;
        const dt = now - this.lastT;
        if (dt > 0) this.velocity = (e.clientX - this.lastX) / dt;
        this.lastX = e.clientX;
        this.lastT = now;

        // 目标位移 + 阻尼
        let target = this.startTranslate + dx;
        if (target > 0) {
            if (this.leftWidth === 0) {
                target = RB(target, this.cardW);
            } else if (target > this.leftWidth) {
                target = this.leftWidth + RB(target - this.leftWidth, this.cardW);
            }
        } else if (target < 0) {
            if (this.rightWidth === 0) {
                target = -RB(-target, this.cardW);
            } else if (-target > this.rightWidth) {
                target = -this.rightWidth - RB(-target - this.rightWidth, this.cardW);
            }
        }
        this.setTranslate(target);

        // 过冲触发线（视觉提示 + 触觉）
        const willTrigger = Math.abs(target) > this.cardW * OVERSHOOT_RATIO;
        if (willTrigger !== this._lastWillTrigger) {
            this._lastWillTrigger = willTrigger;
            if (willTrigger) {
                navigator.vibrate?.(10);
                this.toggleOvershootVisual(true);
            } else {
                this.toggleOvershootVisual(false);
            }
        }
    }

    onPointerUp(e) {
        if (!this.dragging) return;
        this.dragging = false;
        const wasLocked = this.locked;
        this.cleanupActive();
        try { this.root.releasePointerCapture(this._activePointerId); } catch { /* ignore */ }
        this._activePointerId = null;

        if (wasLocked !== 'horizontal') return;

        const t = this.translate;
        const v = this.velocity;
        const dist = Math.hypot(e.clientX - this.startX, e.clientY - this.startY);

        // 视为点击（移动距离极小）
        if (dist < CLICK_TOLERANCE && Math.abs(t) < CLICK_TOLERANCE) {
            this.close(false);
            return;
        }

        const overshoot = this.cardW * OVERSHOOT_RATIO;
        if (Math.abs(t) > overshoot) {
            // 触发主动作
            const side = t > 0 ? 'left' : 'right';
            this.flyOutAndTrigger(side);
            return;
        }

        const side = t > 0 ? 'left' : (t < 0 ? 'right' : null);
        const width = side === 'left' ? this.leftWidth : (side === 'right' ? this.rightWidth : 0);
        const ratio = width > 0 ? Math.abs(t) / width : 0;
        const fast = Math.abs(v) > FAST_SWIPE_VELOCITY;
        const fastSameDir = fast && Math.sign(v) === Math.sign(t);
        const shouldOpen = side && (ratio > SNAP_OPEN_RATIO || fastSameDir);

        if (shouldOpen) this.open(side);
        else this.close(true);
    }

    cleanupActive() {
        this.root.removeEventListener('pointermove', this.onPointerMove);
        this.root.removeEventListener('pointerup', this.onPointerUp);
        this.root.removeEventListener('pointercancel', this.onPointerUp);
    }

    // ───────────── 视觉控制 ─────────────

    setTranslate(x) {
        this.translate = x;
        this.content.style.transform = `translate3d(${x}px, 0, 0)`;
        if (this.leftEl) {
            const w = x > 0 ? Math.min(x, this.cardW) : 0;
            this.leftEl.style.width = `${w}px`;
        }
        if (this.rightEl) {
            const w = x < 0 ? Math.min(-x, this.cardW) : 0;
            this.rightEl.style.width = `${w}px`;
        }
    }

    toggleOvershootVisual(active) {
        // 给 primary 按钮加 data-overshoot 标记，CSS 控制视觉
        const side = this.translate > 0 ? this.leftEl : this.rightEl;
        if (!side) return;
        if (active) side.setAttribute('data-overshoot', '');
        else side.removeAttribute('data-overshoot');
    }

    // ───────────── 状态切换 ─────────────

    open(side) {
        const width = side === 'left' ? this.leftWidth : this.rightWidth;
        if (width <= 0) { this.close(false); return; }
        const target = side === 'left' ? width : -width;

        this.animateTo(target, () => {
            this.opened = true;
            this.openSide = side;
            openInstances.add(this);
            this.bindCloseListeners();
            this.dotnet?.invokeMethodAsync('NotifyOpened', side).catch(() => {});
        });
    }

    close(notify) {
        const wasOpen = this.opened;

        // DOM 已被父组件移除（如删除回调成功后行被移除）→ 立即清理状态，无动画
        if (!this.content || !this.root.isConnected) {
            this.opened = false;
            this.openSide = null;
            openInstances.delete(this);
            this.unbindCloseListeners();
            return;
        }

        this.animateTo(0, () => {
            this.opened = false;
            this.openSide = null;
            this.toggleOvershootVisual(false);
            openInstances.delete(this);
            this.unbindCloseListeners();
            if (wasOpen && notify) {
                this.dotnet?.invokeMethodAsync('NotifyClosed').catch(() => {});
            }
        });
    }

    flyOutAndTrigger(side) {
        const target = side === 'left' ? this.cardW : -this.cardW;

        // 飞出语义上算 open：注册 registry + 绑定关闭 listener，
        // 让用户其他操作（点其他卡片/滚动/外部点击/ESC）都能正常关闭
        this.opened = true;
        this.openSide = side;
        openInstances.add(this);
        this.bindCloseListeners();

        const triggerAndRecover = () => {
            const p = this.dotnet?.invokeMethodAsync('TriggerPrimary', side);
            const recover = () => this.close(false);
            if (p && typeof p.finally === 'function') {
                p.finally(recover);
            } else {
                recover();
            }
        };

        if (reducedMotion()) {
            this.setTranslate(target);
            triggerAndRecover();
            return;
        }
        this.content.style.transition = TRANSITION;
        let triggered = false;
        const triggerOnce = () => {
            if (triggered) return;
            triggered = true;
            this.content.removeEventListener('transitionend', onEnd);
            triggerAndRecover();
        };
        const onEnd = () => triggerOnce();
        this.content.addEventListener('transitionend', onEnd, { once: true });
        setTimeout(triggerOnce, 280);
        this.setTranslate(target);
    }

    animateTo(x, cb) {
        if (reducedMotion()) {
            this.setTranslate(x);
            cb?.();
            return;
        }
        this.content.style.transition = TRANSITION;
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            this.content.removeEventListener('transitionend', onEnd);
            this.content.style.transition = 'none';
            cb?.();
        };
        const onEnd = () => finish();
        this.content.addEventListener('transitionend', onEnd, { once: true });
        // 兜底：transitionend 不触发时 280ms 后强制完成
        setTimeout(finish, 280);
        this.setTranslate(x);
    }

    // ───────────── 关闭路径监听（仅在打开时绑定） ─────────────

    bindCloseListeners() {
        document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
        document.addEventListener('keydown', this.onDocumentKeyDown);
        if (this.scrollParent === window) {
            window.addEventListener('scroll', this.onScroll, { passive: true });
        } else {
            this.scrollParent.addEventListener('scroll', this.onScroll, { passive: true });
        }
    }

    unbindCloseListeners() {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
        if (this.scrollParent === window) {
            window.removeEventListener('scroll', this.onScroll);
        } else {
            this.scrollParent.removeEventListener('scroll', this.onScroll);
        }
    }

    onDocumentPointerDown(e) {
        if (this.root.contains(e.target)) return;
        this.close(true);
    }

    onDocumentKeyDown(e) {
        if (e.key === 'Escape') this.close(true);
    }

    onScroll() {
        const now = performance.now();
        if (now - this._scrollLast < SCROLL_CLOSE_THROTTLE) return;
        this._scrollLast = now;
        this.close(true);
    }

    onContextMenu(e) {
        // 防长按右键菜单（仅滑动区域）
        if (this.dragging || this.opened) e.preventDefault();
    }

    // ───────────── Dispose ─────────────

    dispose() {
        this.cleanupActive();
        this.unbindCloseListeners();
        this.root.removeEventListener('pointerdown', this.onPointerDown);
        this.root.removeEventListener('contextmenu', this.onContextMenu);
        this.resizeObserver?.disconnect();
        openInstances.delete(this);
        this.dotnet = null;
    }
}

// ───────────── 全局 API（C# 调用） ─────────────

const controllers = new Map(); // id → SwipeController

function init(elementId, dotnetRef) {
    const el = document.getElementById(elementId);
    if (!el) return;
    // 防重复 init
    if (controllers.has(elementId)) controllers.get(elementId).dispose();
    controllers.set(elementId, new SwipeController(el, dotnetRef));
}

function close(elementId) {
    controllers.get(elementId)?.close(false);
}

function destroy(elementId) {
    const c = controllers.get(elementId);
    if (c) {
        c.dispose();
        controllers.delete(elementId);
    }
}

// 挂全局
window.ArkUI = window.ArkUI || {};
window.ArkUI.SwipeAction = { init, close, destroy };
